import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Button, DatePicker, Dropdown, Empty, Space} from 'antd';
import dayjs from 'dayjs';
import {connect} from 'react-redux';
import {CloseOutlined, PlusOutlined} from '@ant-design/icons';
import {
  StyledInvoiceSelector,
  StyledInvoiceSelectorActionsSpace,
  StyledInvoiceSelectorBr,
  StyledInvoiceSelectorHeader,
  StyledInvoiceSelectorList,
  StyledInvoiceSelectorMatchSpace,
} from './StyledInvoiceSelector';
import Search from '../../../Search';
import SpinSmall from '../../../SpinSmall';
import {
  StyledTransactionMatchingPanelAmountSpace,
  StyledTransactionMatchingPanelFilterSpace
} from '../../InvoicePage/TransactionMatchingPanel/StyledTransactionMatchingPanel';
import InvoiceRadioSelector from '../../TransactionsPage/InvoiceRadioSelector';
import UploadInvoicesModal from '../../InvoicesPage/UploadInvoicesModal';
import {accountingHelpers, dateHelpers, invoicesHelpers} from '../../../../utils/helpers';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {
  DEFAULT_MODAL_PROPS,
  DEFAULT_PAGE_SIZE,
  expenseTypesConstants,
  invoiceStatusConstants
} from '../../../../constants';
import {expenseActions} from '../../../../state/actions/expense.actions';

const amountTypeOptions = [
  { key: '<', label: '≤' },
  { key: '>', label: '≥' }
];

const defaultFilter = {
  amount: '',
  amountType: '<',
  dateRange: null,
  search: ''
};

const defaultInvoices = {
  cursor: null,
  data: [],
  loading: false
};

const searchProps = {
  allowClear: true,
  minSearchSymbolsLength: 1
};

const InvoiceSelector = ({
  dispatch,
  batchCreateInvoices,
  getInvoices,
  linkExpense,
  updateExpense,

  onSuccess,
  transaction,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'accounting', 'invoices']);
  const [filter, setFilter] = useState(defaultFilter);
  const [invoices, setInvoices] = useState(defaultInvoices);
  const [selectedValue, setSelectedValue] = useState(null);
  const [uploadInvoicesModalProps, setUploadInvoicesModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [noReceiptButtonProps, setNoReceiptButtonProps] = useState({loading: false});
  const [loading, setLoading] = useState(false);

  const handleCancelUploadInvoicesModal = () => setUploadInvoicesModalProps({...uploadInvoicesModalProps, loading: false, open: false});

  const handleOpenUploadInvoicesModal = () => setUploadInvoicesModalProps({...uploadInvoicesModalProps, open: true});

  const finishModalLoading = () => setUploadInvoicesModalProps({...uploadInvoicesModalProps, loading: false});

  const finishLoading = () => setLoading(false);

  const amountTypeLabel = useMemo(
    () => amountTypeOptions.find(({ key }) => key === filter.amountType)?.label || '',
    [filter.amountType]
  );

  const getVendorName = (transaction) => transaction?.expense?.vendor || '';

  const headerProps = useMemo(() => {
    if (!transaction) return { amount: '', date: '', vendorName: '' };

    return {
      amount: accountingHelpers.getEntryAmount(transaction),
      date: dateHelpers.getDateFromISO(transaction.transaction_date, 'DD MMM YYYY, HH:mm'),
      vendorName: getVendorName(transaction)
    };
  }, [transaction]);

  useEffect(() => {
    if (transaction) {
      const search = getVendorName(transaction);
      setFilter(prevState => ({
        ...prevState,
        amount: '',
        dateRange: null,
        search
      }));
    } else if (invoices.data.length > 0) {
      setInvoices(prevState => ({...prevState, data: []}));
      setSelectedValue(null);
    }
  }, [transaction]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadInvoices = (queryParams) => {
    const query = {
      ...invoicesHelpers.convertMatchFilterPropsToApi(queryParams),
      per_page: DEFAULT_PAGE_SIZE,
      status_not_eq: invoiceStatusConstants.MATCHED
    };

    setInvoices(prevState => ({...prevState, loading: true}));
    getInvoices({
      query,
      successCallback: (response) => {
        setInvoices(prevState => ({
          ...prevState,
          data: response.invoices,
          loading: false,
        }));
      },
      errorCallback: () => {
        setInvoices(prevState => ({
          ...prevState,
          loading: false,
        }));
      }
    });
  }

  const handleOnFilter = (key, value) => {
    const filterParams = {...filter, [key]: value};
    setFilter(filterParams);
    handleLoadInvoices(filterParams);
  }

  const handleSelect = (e, value) => setSelectedValue(value);

  const handleUnselect = () => setSelectedValue(null);

  const handleLinkExpense = (invoiceId) => {
    const expenseId = transaction?.expense_id;

    if (!expenseId || !invoiceId) {
      return;
    }

    setLoading(true);

    linkExpense({
      id: invoiceId,
      data: { expense_id: expenseId },
      successCallback: () => {
        if (uploadInvoicesModalProps.open) {
          setUploadInvoicesModalProps({...uploadInvoicesModalProps, open: false});
        }

        onSuccess?.({ ...transaction, invoice_id: invoiceId });
        finishLoading();
      },
      errorCallback: finishLoading
    })
  }

  const handleNoReceiptClick = () => {
    const expenseId = transaction?.expense_id;

    if (!expenseId) {
      return;
    }

    setNoReceiptButtonProps({loading: true});

    updateExpense({
      id: expenseId,
      data: { expense_type: expenseTypesConstants.NO_RECEIPT },
      successCallback: () => {
        onSuccess?.();
        setNoReceiptButtonProps({loading: false});
      },
      errorCallback: () => {
        setNoReceiptButtonProps({loading: false});
      }
    });
  }

  const handleOkUploadInvoicesModal = (invoices) => {
    setUploadInvoicesModalProps({...uploadInvoicesModalProps, loading: true});

    batchCreateInvoices(
      { invoices },
      (data) => {
        const invoiceId = data[0]?.id;
        if (invoiceId) {
          handleLinkExpense(invoiceId);
        }
        finishModalLoading();
      },
      finishModalLoading
    )
  }

  const handleMatchInvoice = () => {
    if (!loading && !selectedValue) {
      return;
    }

    handleLinkExpense(selectedValue);
  }

  return (
    <StyledInvoiceSelector direction='vertical' size='middle' {...rest}>
      <StyledInvoiceSelectorHeader>
        <Space size={0} direction='vertical'>
          <span className='invoice-selector-header-date'>
            {headerProps.date}
          </span>
          <span className='invoice-selector-header-vendor'>
            {headerProps.vendorName}
          </span>
        </Space>
        <span className='invoice-selector-header-amount'>
          {headerProps.amount}
        </span>
      </StyledInvoiceSelectorHeader>

      <StyledTransactionMatchingPanelFilterSpace size='small'>
        <Search
          {...searchProps}
          defaultValue={filter.search}
          onSearch={(value) => handleOnFilter('search', value)}
          placeholder={t('search')}
          value={filter.search}
          size='middle'
        />

        <StyledTransactionMatchingPanelAmountSpace size={0}>
          <Search
            {...searchProps}
            defaultValue={filter.amount}
            placeholder={t('amount')}
            onSearch={(value) => handleOnFilter('amount', value)}
            suffix={undefined}
            style={{ width: '100%' }}
            type='number'
            value={filter.amount}
          />
          <Dropdown
            menu={{
              items: amountTypeOptions,
              onClick: (e) => handleOnFilter('amountType', e.key)
            }}
            trigger={['click']}
          >
            <Button>
              {amountTypeLabel}
            </Button>
          </Dropdown>
        </StyledTransactionMatchingPanelAmountSpace>

        <DatePicker.RangePicker
          disabledDate={(current) => current.isAfter(dayjs())}
          panelRender={dateHelpers.getDatePickerPanelRender}
          onChange={(value) => handleOnFilter('dateRange', value)}
          format='DD MMM YYYY'
          size='middle'
          superNextIcon={false}
          superPrevIcon={false}
          value={filter.dateRange}
        />
      </StyledTransactionMatchingPanelFilterSpace>

      <StyledInvoiceSelectorList>
        <SpinSmall spinning={invoices.loading}>
          {invoices.data.length > 0 ? (
            <InvoiceRadioSelector
              invoices={invoices.data}
              isPreviewAvailable={false}
              onChange={handleSelect}
              selected={selectedValue}
            />
          ) : !invoices.loading && (
            <Empty
              description={(
                <Space size={4} direction='vertical'>
                  <strong>{t('accounting:noSearchInvoicesResults.title')}</strong>
                  <span>{t('accounting:noSearchInvoicesResults.description')}</span>
                </Space>
              )}
              style={{ maxWidth: 390, margin: 'auto' }}
            />
          )}
        </SpinSmall>
      </StyledInvoiceSelectorList>

      {selectedValue ? (
        <StyledInvoiceSelectorMatchSpace size='small'>
          <Button
            onClick={handleUnselect}
            icon={<CloseOutlined />}
            type='link'
          >
            {t('unselect')}
          </Button>
          <Button
            loading={loading}
            onClick={handleMatchInvoice}
            size='middle'
            type='primary'
          >
            {t('match')}
          </Button>
        </StyledInvoiceSelectorMatchSpace>
      ) : (
        <StyledInvoiceSelectorActionsSpace direction='vertical' size='middle'>
          <StyledInvoiceSelectorBr>{t('or')}</StyledInvoiceSelectorBr>
          <Space size='small'>
            <Button
              icon={<PlusOutlined />}
              onClick={handleOpenUploadInvoicesModal}
              size='middle'
              type='primary'
            >
              {t('bill')}
            </Button>
            <Button
              {...noReceiptButtonProps}
              className='primary-outline'
              onClick={handleNoReceiptClick}
              size='middle'
            >
              {t('expenseTypes.noReceipt')}
            </Button>
          </Space>
        </StyledInvoiceSelectorActionsSpace>
      )}

      <UploadInvoicesModal
        {...uploadInvoicesModalProps}
        onCancel={handleCancelUploadInvoicesModal}
        onOk={handleOkUploadInvoicesModal}
      />
    </StyledInvoiceSelector>
  );
}

InvoiceSelector.propTypes = {
  transaction: PropTypes.shape({
    lines: PropTypes.arrayOf(PropTypes.shape({
      line_credit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      line_debit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
    vendor_id: PropTypes.string
  })
}

const mapDispatchToProps = {
  batchCreateInvoices: invoicesActions.batchCreateInvoices,
  getInvoices: invoicesActions.getInvoices,
  linkExpense: invoicesActions.linkExpense,
  updateExpense: expenseActions.updateExpense,
}

export default connect(null, mapDispatchToProps)(InvoiceSelector);
